/**
 * invoiceCalc — EINE Berechnung des Rechnungsbetrags für PDF, Buchung und
 * Zahlungsabgleich (vorher rechnete das PDF den Kundenrabatt ab, die
 * Buchung aber nicht → gebuchter Betrag höher als die Rechnung).
 *
 * Regeln (wie RechnungPDF):
 *   Position  = Menge × Einzelpreis × (1 − Positionsrabatt %)
 *   Netto     = Σ Positionen − Kundenrabatt % (auf Netto)
 *   MwSt      = Netto × Satz
 *   Rundung   = erst auf Summen (Cent), Stückpreise bleiben bis 4 Stellen
 *
 * Ohne Imports — auch serverseitig nutzbar.
 */

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {Array<{quantity:number|string, unit_price:number|string, rabatt_prozent?:number|string}>} items
 * @param {{ taxRate?: number, kundenrabattProzent?: number }} opts
 */
export function computeInvoiceTotals(items, { taxRate = 19, kundenrabattProzent = 0 } = {}) {
  const rawSubtotal = (items || []).reduce((sum, it) => {
    const rabatt = Math.min(100, Math.max(0, num(it?.rabatt_prozent)));
    return sum + num(it?.quantity) * num(it?.unit_price) * (1 - rabatt / 100);
  }, 0);
  const subtotal = round2(rawSubtotal);
  const rabattProzent = Math.min(100, Math.max(0, num(kundenrabattProzent)));
  const kundenrabattBetrag = rabattProzent > 0 ? round2(subtotal * (rabattProzent / 100)) : 0;
  const netto = round2(subtotal - kundenrabattBetrag);
  const rate = num(taxRate);
  const taxAmount = round2(netto * (rate / 100));
  return {
    subtotal,
    kundenrabattProzent: rabattProzent,
    kundenrabattBetrag,
    netto,
    taxRate: rate,
    taxAmount,
    total: round2(netto + taxAmount),
  };
}

/**
 * Skonto auf den Bruttobetrag.
 * @returns {{ betrag: number, zahlbetrag: number, bis: string } | null}
 */
export function skontoInfo({ total, invoiceDate, skontoProzent, skontoTage }) {
  const p = num(skontoProzent);
  const tage = Math.floor(num(skontoTage));
  if (p <= 0 || tage <= 0 || !invoiceDate) return null;
  const betrag = round2(num(total) * (p / 100));
  const d = new Date(`${String(invoiceDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + tage);
  return { betrag, zahlbetrag: round2(num(total) - betrag), bis: d.toISOString().slice(0, 10) };
}

/**
 * Mehrere Steuersätze (manuelle Rechnungen): je Satz eine Gruppe, exakt wie
 * die DB-Funktion _fibu_invoice_totals rechnet.
 * @param {Array<{quantity, unit_price, rabatt_prozent?, tax_rate?}>} items
 */
export function computeTaxGroups(items, { kundenrabattProzent = 0, defaultRate = 19 } = {}) {
  const raw = new Map();
  (items || []).forEach((it) => {
    const rate = Number.isFinite(Number(it?.tax_rate)) && it?.tax_rate !== null && it?.tax_rate !== ''
      ? Number(it.tax_rate) : num(defaultRate);
    const rabatt = Math.min(100, Math.max(0, num(it?.rabatt_prozent)));
    raw.set(rate, (raw.get(rate) || 0) + num(it?.quantity) * num(it?.unit_price) * (1 - rabatt / 100));
  });
  const p = Math.min(100, Math.max(0, num(kundenrabattProzent)));
  const groups = [...raw.entries()].sort((a, b) => b[0] - a[0]).map(([taxRate, sum]) => {
    const subtotal = round2(sum);
    const rabatt = p > 0 ? round2(subtotal * (p / 100)) : 0;
    const net = round2(subtotal - rabatt);
    const tax = round2(net * (taxRate / 100));
    return { taxRate, subtotal, rabatt, net, tax, gross: round2(net + tax) };
  });
  const add = (k) => round2(groups.reduce((s, g) => s + g[k], 0));
  return {
    subtotal: add('subtotal'),
    kundenrabattBetrag: add('rabatt'),
    netto: add('net'),
    taxAmount: add('tax'),
    total: add('gross'),
    groups,
  };
}
